import { createClient } from '@supabase/supabase-js';

const getSupabaseConfig = () => {
  if (typeof window !== 'undefined') {
    const settings = localStorage.getItem('lilac_supabase_settings');
    if (settings) {
      const parsed = JSON.parse(settings);
      if (parsed.enabled && parsed.url && parsed.key) {
        return { url: parsed.url, anon: parsed.key };
      }
    }
  }
  return {
    url: process.env.REACT_APP_SUPABASE_URL,
    anon: process.env.REACT_APP_SUPABASE_ANON_KEY
  };
};

const config = getSupabaseConfig();
if (!config.url || !config.anon) {
  console.warn('[SB] Missing Supabase env vars');
}

const nativeFetch = typeof window !== 'undefined' ? window.fetch.bind(window) : fetch;

// ─── Single stable client ────────────────────────────────────────────────────
// persistSession: false — Supabase NEVER touches localStorage automatically.
// We manually store / restore the session for "Remember Me" via setSession().
// This avoids the race where Supabase loads an old session into memory at
// module-init time before AuthProvider can clear it.
export const supabase = createClient(config.url, config.anon, {
  auth: {
    persistSession: false,
    autoRefreshToken: true,
    detectSessionInUrl: false,
  },
  realtime: { params: { eventsPerSecond: 10 } },
  global: {
    headers: { 'x-client-info': 'lilac-valley-crm' },
    fetch: async (url, options = {}) => {
      const maxRetries = 2;
      let lastError;
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 15000);
          const response = await nativeFetch(url, { ...options, signal: controller.signal });
          clearTimeout(timeoutId);
          return response;
        } catch (error) {
          lastError = error;
          if (error.name === 'AbortError' && attempt < maxRetries) {
            await new Promise(r => setTimeout(r, 1000 * attempt));
            continue;
          }
          throw error;
        }
      }
      throw lastError;
    }
  }
});

// ─── Remember Me storage keys ────────────────────────────────────────────────
const RM_FLAG_KEY    = 'crm_remember_me';
const RM_SESSION_KEY = 'crm_rm_session';

export const getRememberMe   = () => { try { return localStorage.getItem(RM_FLAG_KEY) === '1'; } catch { return false; } };
export const setRememberMeOn = () => { try { localStorage.setItem(RM_FLAG_KEY, '1'); } catch {} };
export const clearRememberMe = () => {
  try {
    localStorage.removeItem(RM_FLAG_KEY);
    localStorage.removeItem(RM_SESSION_KEY);
  } catch {}
};

export const saveSession = (session) => {
  try {
    if (session?.access_token && session?.refresh_token) {
      localStorage.setItem(RM_SESSION_KEY, JSON.stringify({
        access_token: session.access_token,
        refresh_token: session.refresh_token,
      }));
    }
  } catch {}
};

export const loadSavedSession = () => {
  try {
    const raw = localStorage.getItem(RM_SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

// ─── Sign out ────────────────────────────────────────────────────────────────
export const signOutAndReset = async () => {
  try { await supabase.auth.signOut(); } catch (err) { console.warn('[SB] signOut err', err); }
  clearRememberMe();
  try { localStorage.removeItem('user_role'); } catch {}
  if (typeof window !== 'undefined') window.location.href = '/login';
};

// Legacy compat
export const recreateSupabaseClient = () => supabase;
export const tryReconnectIfSession  = async () => {
  const { data } = await supabase.auth.getSession();
  return data?.session ?? null;
};

if (typeof window !== 'undefined') window.supabase = supabase;